import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

/**
 * Picture data for hover preview
 */
export interface PreviewPicture {
  uuid: string;
  thumb_url?: string;
  original_url?: string;
  created_at?: string;
  direction?: number;
  siteName?: string;
}

/**
 * PictureHoverPreviewComponent
 *
 * Displays a hover preview tooltip for picture markers on the map.
 * Follows best UX practices:
 * - Appears on hover with slight delay (prevents flicker)
 * - Shows larger preview of thumbnail
 * - For multiple pictures: auto-slides through images
 * - Displays picture count indicator (dots)
 * - Smooth fade-in animation
 * - Smart positioning to stay within viewport
 */
@Component({
    selector: 'app-picture-hover-preview',
    templateUrl: './picture-hover-preview.component.html',
    styleUrls: ['./picture-hover-preview.component.scss'],
    standalone: false
})
export class PictureHoverPreviewComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() pictures: PreviewPicture[] = [];
  @Input() screenPoint: { x: number; y: number };

  @ViewChild('previewContainer') previewContainer: ElementRef;

  // Position for the preview
  x = 0;
  y = 0;

  // Current slide index for multi-picture slideshow
  currentIndex = 0;

  // Track image load errors
  imageError = false;

  // Auto-slide interval reference
  private slideInterval: any = null;

  // Slideshow timing (ms)
  private readonly SLIDE_DURATION = 2500; // Time per slide
  private readonly TRANSITION_DURATION = 400; // CSS transition time

  // Track if component is visible (for cleanup)
  private isVisible = true;

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Start slideshow if multiple pictures
    if (this.pictures.length > 1) {
      this.startSlideshow();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When screenPoint or pictures change, recalculate position
    if (changes['screenPoint'] || changes['pictures']) {
      // Reset slideshow index and error state when pictures change
      if (changes['pictures'] && !changes['pictures'].firstChange) {
        this.currentIndex = 0;
        this.imageError = false;
        this.stopSlideshow();
        if (this.pictures.length > 1) {
          this.startSlideshow();
        }
      }

      // Recalculate position after view updates
      if (this.previewContainer) {
        setTimeout(() => {
          this.adjustPosition();
          this.cdRef.detectChanges();
        }, 0);
      }
    }
  }

  ngAfterViewInit(): void {
    this.adjustPosition();
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.isVisible = false;
    this.stopSlideshow();
  }

  /**
   * Get the current picture to display
   */
  get currentPicture(): PreviewPicture | null {
    if (!this.pictures || this.pictures.length === 0) return null;
    return this.pictures[this.currentIndex];
  }

  /**
   * Get the image URL for display (prefer thumb_url for faster loading)
   */
  get currentImageUrl(): string {
    const pic = this.currentPicture;
    if (!pic) return '';
    return pic.thumb_url || pic.original_url || '';
  }

  /**
   * Check if there are multiple pictures
   */
  get hasMultiplePictures(): boolean {
    return this.pictures.length > 1;
  }

  /**
   * Maximum number of dots to display
   */
  private readonly MAX_VISIBLE_DOTS = 5;

  /**
   * Check if we should show dots (only for small sets)
   */
  get showDots(): boolean {
    return this.pictures.length > 1 && this.pictures.length <= this.MAX_VISIBLE_DOTS;
  }

  /**
   * Check if we should show counter instead of dots (for large sets)
   */
  get showCounter(): boolean {
    return this.pictures.length > this.MAX_VISIBLE_DOTS;
  }

  /**
   * Get formatted date for display
   */
  getFormattedDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  }

  /**
   * Get compass direction text
   */
  getDirectionText(direction: number): string {
    if (direction == null || isNaN(direction)) return '';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((direction % 360) + 360) % 360 / 45) % 8;
    return `${Math.round(direction)}° ${directions[index]}`;
  }

  /**
   * Handle image load error
   */
  onImageError(): void {
    this.imageError = true;
    this.cdRef.detectChanges();
  }

  /**
   * Start auto-slideshow for multiple pictures
   */
  private startSlideshow(): void {
    this.stopSlideshow(); // Clear any existing interval

    this.slideInterval = setInterval(() => {
      if (!this.isVisible) {
        this.stopSlideshow();
        return;
      }
      this.nextSlide();
    }, this.SLIDE_DURATION);
  }

  /**
   * Stop the slideshow
   */
  private stopSlideshow(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = null;
    }
  }

  /**
   * Move to the next slide
   */
  private nextSlide(): void {
    this.currentIndex = (this.currentIndex + 1) % this.pictures.length;
    this.imageError = false; // Reset error state for new image
    this.cdRef.detectChanges();
  }

  /**
   * Adjust position to stay within viewport
   * Positions preview above/below and left/right of cursor based on available space
   */
  private adjustPosition(): void {
    const map = document.getElementById('projectMap');
    if (!map || !this.previewContainer) return;

    const mapRect = map.getBoundingClientRect();
    const preview = this.previewContainer.nativeElement.getBoundingClientRect();
    const { x, y } = this.screenPoint;

    // Preview dimensions (with some padding)
    const previewWidth = preview.width || 260;
    const previewHeight = preview.height || 220;
    const markerSize = 44; // Approximate size of the picture marker thumbnail
    const gap = 12; // Gap between marker and preview

    // Calculate horizontal position
    // Position to the right of the marker, with gap to avoid overlap
    const rightPosition = x + markerSize + gap;
    const leftPosition = x - previewWidth - gap;

    if (rightPosition + previewWidth < mapRect.width) {
      // Enough space on the right
      this.x = rightPosition;
    } else if (leftPosition > 0) {
      // Use left side
      this.x = leftPosition;
    } else {
      // Fallback: center it horizontally
      this.x = Math.max(8, (mapRect.width - previewWidth) / 2);
    }

    // Calculate vertical position
    // Position above or below the marker to avoid overlap
    const abovePosition = y - previewHeight - gap;
    const belowPosition = y + markerSize + gap;

    if (abovePosition > 0) {
      // Enough space above
      this.y = abovePosition;
    } else if (belowPosition + previewHeight < mapRect.height) {
      // Use below
      this.y = belowPosition;
    } else {
      // Fallback: center vertically
      this.y = Math.max(8, (mapRect.height - previewHeight) / 2);
    }
  }
}
